import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { getPblCases } from '@/api/pbl.api';
import PblCaseList from '@/components/Pbl/CaseList';
import {
  addPblDashboardFilterOption,
  getPblDashboardCaseFilter,
  getPblDashboardCaseStatuses,
  isPblDashboardCaseFilter,
  PBL_DASHBOARD_FILTER_PARAM,
} from '@/components/Pbl/CaseList/dashboardFilter';
import PATH from '@/router/path';
import useTableRequestState from '@/hooks/useTableRequestState';
import { translatePblDashboardLabel } from '@/utils/pblDisplayTranslation';
import { errorHandler } from '@/utils/toastHandler';

import { getPblOcManageTableCases, getPblOcPendingReviewCount, mapPblCaseToOcManagedCase } from './caseReviewState';
import { loadOcCasePages } from './loadCasePages';
import type { PblOcCaseFilter, PblOcManagedCase, PblOcManagedCaseStatus } from './types';
import { useTranslation } from 'react-i18next';

const PblOcManageCases = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardFilter = getPblDashboardCaseFilter(searchParams);
  const [rows, setRows] = useState<PblOcManagedCase[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const { requestState, beginRequest, completeRequest, failRequest, retryKey, retry } = useTableRequestState(
    rows.length > 0
  );
  const tableRows = useMemo(() => getPblOcManageTableCases(rows), [rows]);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const statusLabels = useMemo<Record<PblOcManagedCaseStatus, string>>(
    () => ({
      in_progress: t('pbl.pagesHardcoded.ocManageCases.inProgress'),
      to_be_rated: t('pbl.pagesHardcoded.ocManageCases.toBeRated'),
      case_closed: t('pbl.pagesHardcoded.ocManageCases.caseClosed'),
      case_cancelled: t('pbl.dashboardCalendarNotifications.dashboard.caseTable.statuses.caseClosed'),
      to_be_confirmed: t('pbl.dashboardCalendarNotifications.dashboard.caseTable.statuses.toBeConfirmed'),
    }),
    [t]
  );
  const filters = useMemo<Array<{ value: PblOcCaseFilter; label: string }>>(
    () => [
      { value: 'all', label: t('pbl.pagesHardcoded.ocManageCases.all') },
      { value: 'in_progress', label: t('pbl.pagesHardcoded.ocManageCases.inProgress') },
      { value: 'to_be_rated', label: t('pbl.pagesHardcoded.ocManageCases.toBeRated') },
      { value: 'case_closed', label: t('pbl.pagesHardcoded.ocManageCases.caseClosed') },
    ],
    [t]
  );
  const activeFilters = useMemo(
    () =>
      addPblDashboardFilterOption(
        filters,
        dashboardFilter,
        dashboardFilter ? translatePblDashboardLabel(t, { key: dashboardFilter, label: dashboardFilter }) : ''
      ),
    [dashboardFilter, filters, t]
  );

  useEffect(() => {
    const loadCases = async () => {
      const requestId = beginRequest();
      try {
        const dashboardStatuses = getPblDashboardCaseStatuses(dashboardFilter);
        const [pages, pendingResponse] = await Promise.all([
          Promise.all(dashboardStatuses?.length ? dashboardStatuses.map(loadOcCasePages) : [loadOcCasePages()]),
          dashboardStatuses?.length
            ? getPblCases({ status: 'pending_oc_review', per_page: 1, page: 1 })
            : Promise.resolve(null),
        ]);
        if (pendingResponse && pendingResponse.data.success !== true) {
          throw new Error(pendingResponse.data.message || t('pbl.pagesHardcoded.ocManageCases.failedToLoadCases'));
        }

        if (!completeRequest(requestId)) return;

        const uniqueCases = new Map(pages.flat().map((item) => [item.id, item]));
        const nextRows = Array.from(uniqueCases.values()).map(mapPblCaseToOcManagedCase);
        setRows(nextRows);
        setPendingReviewCount(pendingResponse ? pendingResponse.data.data.total : getPblOcPendingReviewCount(nextRows));
        setTotalEntries(getPblOcManageTableCases(nextRows).length);
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.data ||
          error.message ||
          t('pbl.pagesHardcoded.ocManageCases.failedToLoadCases');
        if (failRequest(requestId, message)) errorHandler(message);
      }
    };

    loadCases();
  }, [beginRequest, completeRequest, dashboardFilter, failRequest, retryKey, t]);

  const updateFilter = (filter: PblOcCaseFilter) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (isPblDashboardCaseFilter(filter)) {
      nextSearchParams.set(PBL_DASHBOARD_FILTER_PARAM, filter);
    } else {
      nextSearchParams.delete(PBL_DASHBOARD_FILTER_PARAM);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const goToDetail = (item: PblOcManagedCase) => {
    if (item.backendStatus === 'pending_oc_review') {
      navigate(PATH.pbl.pendingReviewDetail.replace(':caseId', String(item.id)));
      return;
    }

    navigate(PATH.pbl.caseDetail.replace(':caseId', String(item.id)));
  };

  return (
    <PblCaseList
      title={t('pbl.pagesHardcoded.ocManageCases.manageCase')}
      rows={tableRows}
      filters={activeFilters}
      initialFilter={dashboardFilter || 'all'}
      onFilterChange={updateFilter}
      primaryAction={{
        label: t('pbl.pagesHardcoded.ocManageCases.toBeConfirmedValue', {
          value1:
            requestState.status === 'loading' || (requestState.status === 'error' && !requestState.hasStaleData)
              ? '—'
              : pendingReviewCount,
        }),
        onClick: () => navigate(PATH.pbl.pendingReviewCases),
      }}
      totalEntries={totalEntries}
      requestState={requestState}
      onRetry={retry}
      searchAriaLabel="Search cases"
      statusLabels={statusLabels}
      onRowClick={goToDetail}
    />
  );
};

export default PblOcManageCases;
